import { useEffect, useRef, useState } from 'react';
import { ImagePlus, SendHorizonal } from 'lucide-react';

export interface ChatComposerProps {
  /** Geeft true bij succes; alleen dán wordt het veld leeggemaakt. Een mislukte
   *  verzending mag nooit de getypte tekst opeten. */
  onSend: (text: string) => Promise<boolean>;
  onTyping: (isTyping: boolean) => void;
  disabled?: boolean;
  busy?: boolean;
  placeholder?: string;
  /** Concept dat buiten dit component leeft. De agent-console heeft dat nodig:
   *  van gesprek wisselen mag geen half getypt antwoord weggooien. */
  value?: string;
  onValueChange?: (value: string) => void;
  autoFocus?: boolean;
  /** Een afbeelding sturen. Weglaten = geen knop. */
  onSendImage?: (file: File) => Promise<boolean>;
}

const TYPING_DEBOUNCE_MS = 2000;

export function ChatComposer({
  onSend, onTyping, disabled, busy, placeholder, value, onValueChange, autoFocus, onSendImage,
}: ChatComposerProps) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [internal, setInternal] = useState('');
  const text = value ?? internal;
  const setText = onValueChange ?? setInternal;

  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const typingUntil = useRef(0);
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (autoFocus) areaRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => () => {
    if (stopTimer.current) clearTimeout(stopTimer.current);
  }, []);

  function handleChange(next: string) {
    setText(next);
    const now = Date.now();
    // Hoogstens één "ik typ"-signaal per twee seconden, en na twee seconden
    // stilte automatisch een "ik typ niet meer" — anders blijft de indicator
    // hangen zodra iemand halverwege een zin wegloopt.
    if (now > typingUntil.current) {
      typingUntil.current = now + TYPING_DEBOUNCE_MS;
      onTyping(true);
    }
    if (stopTimer.current) clearTimeout(stopTimer.current);
    stopTimer.current = setTimeout(() => {
      typingUntil.current = 0;
      onTyping(false);
    }, TYPING_DEBOUNCE_MS);
  }

  async function submit() {
    const trimmed = text.trim();
    if (!trimmed || disabled || busy) return;
    if (stopTimer.current) clearTimeout(stopTimer.current);
    typingUntil.current = 0;
    onTyping(false);
    const sent = await onSend(trimmed);
    if (sent) setText('');
    areaRef.current?.focus();
  }

  return (
    <form
      className="flex items-end gap-2 border-t border-hairline bg-surface px-3 py-2.5"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {/* Een <textarea> en niet een <input>: in het bronproject was dit een
          input (ChatWidget.tsx:592), waardoor een klant geen meerregelige
          foutmelding kon plakken. Enter verstuurt, Shift+Enter is een nieuwe regel.

          De hoogte komt uit min-h-* en niet uit de tekstgrootte: theme.css:527
          forceert onder 640px `font-size: 16px !important` op invoervelden, dus
          een hoogte die van de tekst afhangt springt op het breekpunt. */}
      <textarea
        ref={areaRef}
        rows={1}
        value={text}
        disabled={disabled}
        placeholder={placeholder ?? 'Typ je bericht…'}
        aria-label="Je bericht"
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void submit();
          }
        }}
        className="max-h-32 min-h-10 flex-1 resize-none rounded-md border border-hairline-strong bg-surface px-3 py-2 text-base text-ink placeholder:text-ink-3 focus:border-accent disabled:opacity-60"
      />
      {onSendImage && (
        <>
          {/* Verborgen invoerveld met een echte knop ervoor: een <input
              type="file"> is niet te stylen en zijn eigen label leest slecht
              voor. */}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = ''; // hetzelfde bestand mag opnieuw gekozen worden
              if (!file) return;
              setUploading(true);
              await onSendImage(file);
              setUploading(false);
            }}
          />
          <button
            type="button"
            disabled={disabled || uploading}
            onClick={() => fileRef.current?.click()}
            aria-label="Afbeelding sturen"
            className="rounded-md border border-hairline-strong px-2.5 py-2 text-ink-2 transition hover:bg-surface-hover hover:text-ink disabled:opacity-40"
          >
            <ImagePlus size={16} aria-hidden />
          </button>
        </>
      )}

      <button
        type="submit"
        disabled={disabled || busy || !text.trim()}
        aria-label="Bericht versturen"
        className="rounded-md bg-accent px-3 py-2 text-accent-on shadow-sm transition hover:bg-accent-hover hover:shadow-glow disabled:opacity-40 disabled:hover:shadow-sm"
      >
        <SendHorizonal size={16} aria-hidden />
      </button>
    </form>
  );
}
